import { createBrowserRouter, Navigate } from 'react-router-dom';
import { UserRole, Permission } from '@/types/global.types';

// Layout
import { DashboardLayout } from '@/shared/components/Layout';

// Auth
import { LoginForm } from '@/features/auth/components/LoginForm';
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute';
import { RequireFinance } from '@/features/auth/components/RequireFinance';
import { RequirePermission } from '@/features/auth/components/RequirePermission';
import { VerifyEmailPage } from '@/features/auth/pages/VerifyEmailPage';
import { SetPasswordPage } from '@/features/auth/pages/SetPasswordPage';
import { ForgotPasswordPage } from '@/features/auth/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/features/auth/pages/ResetPasswordPage';
import { SuspendedAccountPage } from '@/features/auth/pages/SuspendedAccountPage';
import { AppDownloadPage } from '@/features/auth/pages/AppDownloadPage';
import { LegalPage } from '@/features/auth/pages/LegalPage';
import { SystemSuspendedPage } from '@/features/auth/components/SystemSuspendedPage';
import { FinancialSuspendedPage } from '@/features/auth/components/FinancialSuspendedPage';

// Marketing (public homepage)
import { RootRoute } from '@/features/auth/components/RootRoute';
import { ConformiteCndpPage } from '@/features/marketing/pages/ConformiteCndpPage';

// Dashboards
import { SuperAdminDashboard } from '@/features/super-admin/pages/Dashboard';
import { InstitutionsPage } from '@/features/super-admin/pages/InstitutionsPage';
import { NomenclaturePage } from '@/features/super-admin/pages/NomenclaturePage';
import { PlatformSettingsPage } from '@/features/super-admin/pages/PlatformSettingsPage';
import { SuperAdminUsersPage } from '@/features/super-admin/pages/SuperAdminUsersPage';
import { InstitutionUsersPage } from '@/features/super-admin/pages/InstitutionUsersPage';
import { AuditLogsPage } from '@/features/super-admin/pages/AuditLogsPage';
import { BackupManagementPage } from '@/features/super-admin/pages/BackupManagementPage';
import { TenantExportsPage } from '@/features/super-admin/pages/TenantExportsPage';
// Analytics Pages
import {
  AnalyticsOverviewPage,
  TestCoveragePage,
  BillingAnalyticsPage,
  EmailAnalyticsPage,
  UserAnalyticsPage,
  EngagementPage,
} from '@/features/super-admin/pages/analytics';

// Billing Management Pages
import { SubscriptionPlansPage } from '@/features/super-admin/pages/billing/SubscriptionPlansPage';
import { SubscriptionsPage } from '@/features/super-admin/pages/billing/SubscriptionsPage';
import { InvoicesPage } from '@/features/super-admin/pages/billing/InvoicesPage';
import { BillingSettingsPage } from '@/features/super-admin/pages/billing/BillingSettingsPage';
import { PLStatementPage } from '@/features/super-admin/pages/billing/PLStatementPage';
import { ExpensesPage } from '@/features/super-admin/pages/billing/ExpensesPage';
import { SuppliersPage } from '@/features/super-admin/pages/billing/SuppliersPage';

// Email CRM Pages
import { EmailTemplatesPage } from '@/features/super-admin/pages/email-crm/EmailTemplatesPage';
import { EmailCampaignsPage } from '@/features/super-admin/pages/email-crm/EmailCampaignsPage';
import { EmailAutomationsPage } from '@/features/super-admin/pages/email-crm/EmailAutomationsPage';
import { ContactsPage } from '@/features/super-admin/pages/email-crm/ContactsPage';
import { DGDashboard } from '@/features/dg/pages/Dashboard';
import { PedagogieDashboard } from '@/features/pedagogie/pages/Dashboard';
import { FiliereDashboard } from '@/features/filiere/pages/Dashboard';
import { FinanceDashboard } from '@/features/finance/pages/Dashboard';
import { FinanceStaffViewPage } from '@/features/finance/pages/StaffViewPage';

// Finance Module Pages
import {
  TuitionFeesPage,
  RemindersConfigPage,
  PayslipsPage,
  ReportingPage,
} from '@/features/finance/pages';
import { PaymentManagementPage } from '@/features/finance/pages/payments/PaymentManagementPage';
import PermanentPayrollPage from '@/features/finance/pages/payroll/PermanentPayrollPage';
import VacatairePayrollPage from '@/features/finance/pages/payroll/VacatairePayrollPage';
import InstitutionBankPage from '@/features/dg/pages/InstitutionBankPage';
import StaffPaymentHistoryPage from '@/features/staff/pages/StaffPaymentHistoryPage';

// Student Payment Pages
import { MyPaymentsPage } from '@/features/student/pages/MyPaymentsPage';

// Student Module Pages
import StudentMyClassesPage from '@/features/student/pages/MyClassesPage';
import StudentClassDetailPage from '@/features/student/pages/ClassDetailPage';
import MySchedulePage from '@/features/student/pages/MySchedulePage';
import MyCalendarPage from '@/features/student/pages/MyCalendarPage';
import ExamSchedulePage from '@/features/student/pages/ExamSchedulePage';
import RattrapageSchedulePage from '@/features/student/pages/RattrapageSchedulePage';
import RequestsDashboardPage from '@/features/student/pages/RequestsDashboardPage';
import CreateRequestPage from '@/features/student/pages/CreateRequestPage';
import RequestDetailPage from '@/features/student/pages/RequestDetailPage';
import TranscriptPage from '@/features/student/pages/TranscriptPage';

import { AdminStaffDashboard } from '@/features/admin-staff/pages/Dashboard';
import { TeacherDashboard, MyClassesPage, ClassDetailPage, TeacherExamsPage } from '@/features/teacher/pages';
import { StudentDashboard } from '@/features/student/pages/Dashboard';
import { ParentDashboard } from '@/features/parent/pages/Dashboard';
import ParentSchedulePage from '@/features/parent/pages/ParentSchedulePage';
import ParentAttendancePage from '@/features/parent/pages/ParentAttendancePage';
import ParentGradesPage from '@/features/parent/pages/ParentGradesPage';
import ParentExamSchedulePage from '@/features/parent/pages/ParentExamSchedulePage';
import ParentPaymentsPage from '@/features/parent/pages/ParentPaymentsPage';
import ParentRattrapageSchedulePage from '@/features/parent/pages/ParentRattrapageSchedulePage';

// Shared Pages
import { ProfilePage } from '@/shared/pages';
import { SecurityPage } from '@/shared/pages/SecurityPage';
import { PrivacySettings } from '@/features/auth/components/PrivacySettings';
import { GestionEtablissementPage } from '@/features/dg/pages/GestionEtablissementPage';
import { GestionCampusPage } from '@/features/dg/pages/GestionCampusPage';
import { DocumentsInstitutionnelsPage } from '@/features/dg/pages/DocumentsInstitutionnelsPage';
import DepartementsPage from '@/features/dg/pages/DepartementsPage';

// User Management Pages
import { StaffManagementPage, StudentManagementPage } from '@/shared/pages/users';
import UserSettingsPage from '@/shared/pages/users/UserSettingsPage';

// New E-DEP Pages
import { SuiviDossiersPage } from '@/features/dg/pages/SuiviDossiersPage';
import { DocumentsEdepPage } from '@/features/dg/pages/DocumentsEdepPage';

// Filières Management Pages
import GestionFilieresPage from '@/features/dg/pages/GestionFilieresPage';
import FiliereDetailPage from '@/features/dg/pages/FiliereDetailPage';

// Groups Management
import GestionGroupesPage from '@/features/dg/pages/GestionGroupesPage';

// Grades Management
import ProfessorMonitoringPage from '@/features/dg/pages/ProfessorMonitoringPage';

// Notes & Absences Management (new dashboard pages)
import GradesDashboardPage from '@/features/dg/pages/GradesDashboardPage';
import JuryDeliberationPage from '@/features/dg/pages/JuryDeliberationPage';
import AttendanceDashboardPage from '@/features/dg/pages/AttendanceDashboardPage';
import ParametresAcademiquesPage from '@/features/dg/pages/ParametresAcademiquesPage';

// Student Request Management (Admin)
import RequestManagementPage from '@/features/dg/pages/RequestManagementPage';

// Course Evaluation
import CourseEvaluationPage from '@/features/dg/pages/CourseEvaluationPage';

// Calendar & Planning Management
import { CalendarSettingsPage } from '@/features/calendar/pages/CalendarSettingsPage';
import { PlanningAnnuelPage } from '@/features/calendar/pages/PlanningAnnuelPage';
import { TimetableManagementPage } from '@/features/calendar/pages/TimetableManagementPage';
import { TimetableViewPage } from '@/features/calendar/pages/TimetableViewPage';
import { SchedulingProgressPage } from '@/features/calendar/pages/SchedulingProgressPage';
import ExamSchedulingPage from '@/features/calendar/pages/ExamSchedulingPage';
import RattrapageSchedulingPage from '@/features/calendar/pages/RattrapageSchedulingPage';

// Placeholder page for unimplemented routes
const PlaceholderPage = ({ title }: { title: string }) => (
  <div className="flex items-center justify-center h-64">
    <div className="text-center">
      <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
      <p className="text-gray-500 mt-2">Cette page est en cours de développement</p>
    </div>
  </div>
);

// Unauthorized page
const UnauthorizedPage = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <h1 className="text-4xl font-bold text-gray-900">403</h1>
      <p className="text-xl text-gray-600 mt-2">Accès non autorisé</p>
      <p className="text-gray-500 mt-1">Vous n'avez pas les permissions pour accéder à cette page.</p>
      <a href="/login" className="mt-4 inline-block text-primary-600 hover:text-primary-700">
        Retour à la connexion
      </a>
    </div>
  </div>
);

// Not Found page
const NotFoundPage = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <h1 className="text-4xl font-bold text-gray-900">404</h1>
      <p className="text-xl text-gray-600 mt-2">Page non trouvée</p>
      <a href="/login" className="mt-4 inline-block text-primary-600 hover:text-primary-700">
        Retour à l'accueil
      </a>
    </div>
  </div>
);

export const router = createBrowserRouter([
  // Public routes
  {
    path: '/login',
    element: <LoginForm />,
  },
  {
    path: '/verify-email',
    element: <VerifyEmailPage />,
  },
  {
    path: '/verify-email/:token',
    element: <VerifyEmailPage />,
  },
  {
    path: '/set-password',
    element: <SetPasswordPage />,
  },
  {
    path: '/set-password/:token',
    element: <SetPasswordPage />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
  },
  {
    path: '/reset-password/:token',
    element: <ResetPasswordPage />,
  },
  {
    path: '/unauthorized',
    element: <UnauthorizedPage />,
  },
  // Public mobile-app download page — the target of the printed QR codes.
  {
    path: '/app',
    element: <AppDownloadPage />,
  },
  // Public legal texts (privacy policy URL required by the app stores)
  {
    path: '/legal',
    element: <Navigate to="/legal/privacy" replace />,
  },
  {
    path: '/legal/:slug',
    element: <LegalPage />,
  },
  {
    path: '/account-suspended',
    element: <SuspendedAccountPage />,
  },
  {
    path: '/institution-suspended',
    element: <SystemSuspendedPage />,
  },
  {
    path: '/financial-suspended',
    element: <FinancialSuspendedPage />,
  },

  // Super Admin routes
  {
    path: '/admin',
    element: (
      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="dashboard" replace /> },
      { path: 'dashboard', element: <SuperAdminDashboard /> },
      // Gestion des Établissements submenu
      { path: 'etablissements', element: <Navigate to="etablissements/institutions" replace /> },
      { path: 'etablissements/institutions', element: <InstitutionsPage /> },
      { path: 'etablissements/utilisateurs', element: <SuperAdminUsersPage /> },
      { path: 'etablissements/utilisateurs/:institutionId', element: <InstitutionUsersPage /> },
      { path: 'etablissements/nomenclature', element: <NomenclaturePage /> },
      // Legacy routes for backwards compatibility
      { path: 'institutions', element: <Navigate to="/admin/etablissements/institutions" replace /> },
      { path: 'nomenclature', element: <Navigate to="/admin/etablissements/nomenclature" replace /> },
      // Billing Management routes
      { path: 'billing', element: <Navigate to="billing/plans" replace /> },
      { path: 'billing/plans', element: <SubscriptionPlansPage /> },
      { path: 'billing/subscriptions', element: <SubscriptionsPage /> },
      { path: 'billing/invoices', element: <InvoicesPage /> },
      { path: 'billing/pnl', element: <PLStatementPage /> },
      { path: 'billing/expenses', element: <ExpensesPage /> },
      { path: 'billing/suppliers', element: <SuppliersPage /> },
      { path: 'billing/settings', element: <BillingSettingsPage /> },
      // Email CRM routes
      { path: 'email-crm', element: <Navigate to="email-crm/campaigns" replace /> },
      { path: 'email-crm/campaigns', element: <EmailCampaignsPage /> },
      { path: 'email-crm/templates', element: <EmailTemplatesPage /> },
      { path: 'email-crm/automations', element: <EmailAutomationsPage /> },
      { path: 'email-crm/contacts', element: <ContactsPage /> },
      // Other routes
      { path: 'reports', element: <Navigate to="analytics/overview" replace /> },
      { path: 'settings', element: <PlatformSettingsPage /> },
      { path: 'audit-logs', element: <AuditLogsPage /> },
      { path: 'backup', element: <BackupManagementPage /> },
      { path: 'exports', element: <TenantExportsPage /> },
      // Analytics routes
      { path: 'analytics', element: <Navigate to="analytics/overview" replace /> },
      { path: 'analytics/overview', element: <AnalyticsOverviewPage /> },
      { path: 'analytics/tests', element: <TestCoveragePage /> },
      { path: 'analytics/users', element: <UserAnalyticsPage /> },
      { path: 'analytics/billing', element: <BillingAnalyticsPage /> },
      { path: 'analytics/email', element: <EmailAnalyticsPage /> },
      { path: 'analytics/engagement', element: <EngagementPage /> },
      { path: 'profile', element: <ProfilePage /> },
      { path: 'security', element: <SecurityPage /> },
          { path: 'privacy', element: <PrivacySettings /> },
    ],
  },

  // Institution routes (multi-tenant)
  {
    path: '/institution/:tenantId',
    children: [
      // Director General routes
      {
        path: 'dg',
        element: (
          <ProtectedRoute
            allowedRoles={[UserRole.DIRECTOR_GENERAL]}
            requireTenant
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <DGDashboard /> },
          // Teaching routes (for staff with isAlsoProfessor: true)
          { path: 'mes-cours', element: <MyClassesPage /> },
          { path: 'mes-cours/:groupeId/module/:moduleElementId', element: <ClassDetailPage /> },
          { path: 'mon-emploi-du-temps', element: <TimetableViewPage /> },
          { path: 'etablissement', element: <Navigate to="etablissement/gestion" replace /> },
          { path: 'etablissement/gestion', element: <GestionEtablissementPage /> },
          { path: 'etablissement/campus', element: <GestionCampusPage /> },
          { path: 'etablissement/departements', element: <DepartementsPage /> },
          { path: 'etablissement/nomenclature', element: <Navigate to="etablissement/departements" replace /> },
          { path: 'etablissement/documents-institutionnels', element: <DocumentsInstitutionnelsPage /> },
          { path: 'etablissement/banque', element: <Navigate to="../gestion-financiere/comptes-bancaires" replace /> },
          { path: 'utilisateurs', element: <Navigate to="utilisateurs/personnel" replace /> },
          { path: 'utilisateurs/personnel', element: <StaffManagementPage /> },
          { path: 'utilisateurs/etudiants', element: <StudentManagementPage /> },
          { path: 'utilisateurs/suivi-dossiers', element: <SuiviDossiersPage /> },
          { path: 'utilisateurs/documents-edep', element: <DocumentsEdepPage /> },
          { path: 'utilisateurs/parametres', element: <UserSettingsPage /> },
          { path: 'gestion-pedagogique', element: <Navigate to="gestion-pedagogique/filieres" replace /> },
          { path: 'gestion-pedagogique/filieres', element: <GestionFilieresPage /> },
          { path: 'academique/filieres', element: <GestionFilieresPage /> },
          { path: 'academique/filieres/:filiereId', element: <FiliereDetailPage /> },
          { path: 'academique/groupes', element: <RequirePermission permission={Permission.GROUP_VIEW}><GestionGroupesPage /></RequirePermission> },
          { path: 'academique/notes', element: <RequirePermission permission={Permission.NOTES_VIEW}><GradesDashboardPage /></RequirePermission> },
          { path: 'academique/jury-deliberation', element: <RequirePermission permission={Permission.JURY_VIEW}><JuryDeliberationPage /></RequirePermission> },
          { path: 'academique/absences', element: <RequirePermission permission={Permission.ATTENDANCE_VIEW}><AttendanceDashboardPage /></RequirePermission> },
          { path: 'academique/parametres-academiques', element: <ParametresAcademiquesPage /> },
          { path: 'academique/demandes-documents', element: <RequestManagementPage /> },
          // Backward compatibility redirects for old notes-absences routes
          { path: 'academique/notes-absences', element: <Navigate to="../academique/notes" replace /> },
          { path: 'academique/notes-absences/notes', element: <Navigate to="../../academique/notes" replace /> },
          { path: 'academique/notes-absences/rattrapages', element: <Navigate to="../../academique/notes" replace /> },
          { path: 'academique/notes-absences/absences', element: <Navigate to="../../academique/absences" replace /> },
          { path: 'academique/notes-absences/parametres', element: <Navigate to="../../academique/parametres-academiques" replace /> },
          // Professor Monitoring (new path under academique)
          { path: 'academique/suivi-professeurs', element: <ProfessorMonitoringPage /> },
          // Course Evaluation
          { path: 'academique/evaluation-cours', element: <CourseEvaluationPage /> },
          // Legacy route - redirect to new path
          { path: 'suivi-professeurs', element: <Navigate to="academique/suivi-professeurs" replace /> },
          // Student Request Management
          { path: 'requests/validate', element: <Navigate to="requests/manage" replace /> },
          { path: 'requests/manage', element: <RequestManagementPage /> },
          // Finance Module Routes (protected by RequireFinance + RequirePermission)
          { path: 'gestion-financiere', element: <RequireFinance><Navigate to="gestion-financiere/frais-scolarite" replace /></RequireFinance> },
          // Frais de Scolarité - Unified page (access requires any finance view permission)
          { path: 'gestion-financiere/frais-scolarite', element: <RequireFinance><RequirePermission permission={[Permission.FINANCE_TUITION_VIEW, Permission.FINANCE_ANCILLARY_VIEW, Permission.FINANCE_DISCOUNT_VIEW, Permission.FINANCE_SCHEDULE_VIEW, Permission.FINANCE_REMINDER_VIEW]} mode="any"><TuitionFeesPage /></RequirePermission></RequireFinance> },
          // Legacy routes redirect to unified page
          { path: 'gestion-financiere/frais-scolarite/grille-tarifaire', element: <RequireFinance><Navigate to="../frais-scolarite" replace /></RequireFinance> },
          { path: 'gestion-financiere/frais-scolarite/echeanciers', element: <RequireFinance><Navigate to="../frais-scolarite" replace /></RequireFinance> },
          { path: 'gestion-financiere/frais-scolarite/frais-annexes', element: <RequireFinance><Navigate to="../frais-scolarite" replace /></RequireFinance> },
          { path: 'gestion-financiere/frais-scolarite/remises-bourses', element: <RequireFinance><Navigate to="../frais-scolarite" replace /></RequireFinance> },
          // Paiements Étudiants - Unified page with accounts and pending transfers
          { path: 'gestion-financiere/paiements', element: <RequireFinance><RequirePermission permission={Permission.FINANCE_PAYMENT_VIEW}><PaymentManagementPage /></RequirePermission></RequireFinance> },
          // Legacy routes redirect to unified page
          { path: 'gestion-financiere/paiements/comptes', element: <RequireFinance><Navigate to="../paiements" replace /></RequireFinance> },
          { path: 'gestion-financiere/paiements/saisie', element: <RequireFinance><Navigate to="../paiements" replace /></RequireFinance> },
          { path: 'gestion-financiere/paiements/factures', element: <RequireFinance><Navigate to="../paiements" replace /></RequireFinance> },
          { path: 'gestion-financiere/paiements/impayes', element: <RequireFinance><Navigate to="../paiements" replace /></RequireFinance> },
          { path: 'gestion-financiere/paiements/relances', element: <RequireFinance><RequirePermission permission={Permission.FINANCE_TUITION_VIEW}><RemindersConfigPage /></RequirePermission></RequireFinance> },
          { path: 'gestion-financiere/paiements/suspensions', element: <RequireFinance><Navigate to="../paiements" replace /></RequireFinance> },
          { path: 'gestion-financiere/paiements/virements-attente', element: <RequireFinance><Navigate to="../paiements" replace /></RequireFinance> },
          // Paie Personnel
          { path: 'gestion-financiere/paie', element: <RequireFinance><Navigate to="gestion-financiere/paie/permanents" replace /></RequireFinance> },
          { path: 'gestion-financiere/paie/permanents', element: <RequireFinance><RequirePermission permission={Permission.PAYROLL_PERMANENT_VIEW}><PermanentPayrollPage /></RequirePermission></RequireFinance> },
          { path: 'gestion-financiere/paie/vacataires', element: <RequireFinance><RequirePermission permission={Permission.PAYROLL_VACATAIRE_VIEW}><VacatairePayrollPage /></RequirePermission></RequireFinance> },
          { path: 'gestion-financiere/paie/bulletins', element: <RequireFinance><RequirePermission permission={Permission.PAYROLL_PERMANENT_VIEW}><PayslipsPage /></RequirePermission></RequireFinance> },
          // Reporting Financier
          { path: 'gestion-financiere/reporting', element: <RequireFinance><RequirePermission permission={[Permission.FINANCE_REPORT_RECOVERY, Permission.FINANCE_REPORT_PAYROLL, Permission.FINANCE_REPORT_FORECAST]}><ReportingPage /></RequirePermission></RequireFinance> },
          // Comptes Bancaires
          { path: 'gestion-financiere/comptes-bancaires', element: <RequireFinance><RequirePermission permission={Permission.INSTITUTION_BANK_VIEW}><InstitutionBankPage /></RequirePermission></RequireFinance> },
          // Legacy routes
          { path: 'finances', element: <RequireFinance><Navigate to="gestion-financiere" replace /></RequireFinance> },
          { path: 'reports', element: <PlaceholderPage title="Rapports" /> },
          { path: 'settings', element: <PlaceholderPage title="Paramètres" /> },
          // Calendar & Planning routes
          { path: 'calendrier', element: <Navigate to="calendrier/planning-annuel" replace /> },
          { path: 'calendrier/planning-annuel', element: <PlanningAnnuelPage /> },
          { path: 'calendrier/emplois-du-temps', element: <RequirePermission permission={Permission.SCHEDULE_VIEW}><TimetableManagementPage /></RequirePermission> },
          { path: 'calendrier/examens', element: <ExamSchedulingPage /> },
          { path: 'calendrier/rattrapages', element: <RattrapageSchedulingPage /> },
          { path: 'calendrier/progression', element: <RequirePermission permission={Permission.SCHEDULE_VIEW}><SchedulingProgressPage /></RequirePermission> },
          { path: 'calendrier/parametres', element: <CalendarSettingsPage /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'security', element: <SecurityPage /> },
          { path: 'privacy', element: <PrivacySettings /> },
        ],
      },

      // Pedagogical Director routes
      {
        path: 'pedagogie',
        element: (
          <ProtectedRoute
            allowedRoles={[UserRole.DIRECTOR_GENERAL, UserRole.PEDAGOGICAL_DIRECTOR]}
            requireTenant
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <PedagogieDashboard /> },
          // Teaching routes (for staff with isAlsoProfessor: true)
          { path: 'mes-cours', element: <MyClassesPage /> },
          { path: 'mes-cours/:groupeId/module/:moduleElementId', element: <ClassDetailPage /> },
          { path: 'mon-emploi-du-temps', element: <TimetableViewPage /> },
          { path: 'etablissement', element: <Navigate to="etablissement/gestion" replace /> },
          { path: 'etablissement/gestion', element: <GestionEtablissementPage /> },
          { path: 'etablissement/campus', element: <GestionCampusPage /> },
          { path: 'etablissement/departements', element: <DepartementsPage /> },
          { path: 'etablissement/nomenclature', element: <Navigate to="etablissement/departements" replace /> },
          { path: 'etablissement/documents-institutionnels', element: <DocumentsInstitutionnelsPage /> },
          { path: 'utilisateurs', element: <Navigate to="utilisateurs/personnel" replace /> },
          { path: 'utilisateurs/personnel', element: <StaffManagementPage /> },
          { path: 'utilisateurs/etudiants', element: <StudentManagementPage /> },
          { path: 'utilisateurs/suivi-dossiers', element: <SuiviDossiersPage /> },
          { path: 'utilisateurs/documents-edep', element: <DocumentsEdepPage /> },
          { path: 'utilisateurs/parametres', element: <UserSettingsPage /> },
          { path: 'gestion-pedagogique', element: <Navigate to="gestion-pedagogique/filieres" replace /> },
          { path: 'gestion-pedagogique/filieres', element: <GestionFilieresPage /> },
          { path: 'academique/filieres', element: <GestionFilieresPage /> },
          { path: 'academique/filieres/:filiereId', element: <FiliereDetailPage /> },
          { path: 'academique/groupes', element: <RequirePermission permission={Permission.GROUP_VIEW}><GestionGroupesPage /></RequirePermission> },
          { path: 'academique/notes', element: <RequirePermission permission={Permission.NOTES_VIEW}><GradesDashboardPage /></RequirePermission> },
          { path: 'academique/jury-deliberation', element: <RequirePermission permission={Permission.JURY_VIEW}><JuryDeliberationPage /></RequirePermission> },
          { path: 'academique/absences', element: <RequirePermission permission={Permission.ATTENDANCE_VIEW}><AttendanceDashboardPage /></RequirePermission> },
          { path: 'academique/parametres-academiques', element: <ParametresAcademiquesPage /> },
          { path: 'academique/demandes-documents', element: <RequestManagementPage /> },
          // Backward compatibility redirects
          { path: 'academique/notes-absences', element: <Navigate to="../academique/notes" replace /> },
          { path: 'academique/notes-absences/notes', element: <Navigate to="../../academique/notes" replace /> },
          { path: 'academique/notes-absences/rattrapages', element: <Navigate to="../../academique/notes" replace /> },
          { path: 'academique/notes-absences/absences', element: <Navigate to="../../academique/absences" replace /> },
          { path: 'academique/notes-absences/parametres', element: <Navigate to="../../academique/parametres-academiques" replace /> },
          // Professor Monitoring (new path under academique)
          { path: 'academique/suivi-professeurs', element: <ProfessorMonitoringPage /> },
          // Course Evaluation
          { path: 'academique/evaluation-cours', element: <CourseEvaluationPage /> },
          // Legacy route - redirect to new path
          { path: 'suivi-professeurs', element: <Navigate to="academique/suivi-professeurs" replace /> },
          // Student Request Management
          { path: 'requests/validate', element: <Navigate to="requests/manage" replace /> },
          { path: 'requests/manage', element: <RequestManagementPage /> },
          { path: 'courses', element: <PlaceholderPage title="Cours" /> },
          { path: 'students', element: <PlaceholderPage title="Étudiants" /> },
          { path: 'teachers', element: <PlaceholderPage title="Professeurs" /> },
          { path: 'schedule', element: <PlaceholderPage title="Emploi du temps" /> },
          { path: 'grades', element: <RequirePermission permission={Permission.NOTES_VIEW}><GradesDashboardPage /></RequirePermission> },
          { path: 'reports', element: <PlaceholderPage title="Rapports" /> },
          // Gestion Financière routes (protected by RequireFinance + RequirePermission)
          { path: 'gestion-financiere', element: <RequireFinance><Navigate to="gestion-financiere/frais-scolarite" replace /></RequireFinance> },
          { path: 'gestion-financiere/frais-scolarite', element: <RequireFinance><RequirePermission permission={[Permission.FINANCE_TUITION_VIEW, Permission.FINANCE_ANCILLARY_VIEW, Permission.FINANCE_DISCOUNT_VIEW, Permission.FINANCE_SCHEDULE_VIEW, Permission.FINANCE_REMINDER_VIEW]} mode="any"><TuitionFeesPage /></RequirePermission></RequireFinance> },
          { path: 'gestion-financiere/paiements', element: <RequireFinance><RequirePermission permission={Permission.FINANCE_PAYMENT_VIEW}><PaymentManagementPage /></RequirePermission></RequireFinance> },
          // Ma Paie (staff own payment history)
          { path: 'ma-paie', element: <RequireFinance><StaffPaymentHistoryPage /></RequireFinance> },
          // Calendar & Planning routes
          { path: 'calendrier', element: <Navigate to="calendrier/planning-annuel" replace /> },
          { path: 'calendrier/planning-annuel', element: <PlanningAnnuelPage /> },
          { path: 'calendrier/emplois-du-temps', element: <RequirePermission permission={Permission.SCHEDULE_VIEW}><TimetableManagementPage /></RequirePermission> },
          { path: 'calendrier/examens', element: <ExamSchedulingPage /> },
          { path: 'calendrier/rattrapages', element: <RattrapageSchedulingPage /> },
          { path: 'calendrier/progression', element: <RequirePermission permission={Permission.SCHEDULE_VIEW}><SchedulingProgressPage /></RequirePermission> },
          { path: 'calendrier/parametres', element: <CalendarSettingsPage /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'security', element: <SecurityPage /> },
          { path: 'privacy', element: <PrivacySettings /> },
        ],
      },

      // Program Head (Chef de Filière) routes
      {
        path: 'filiere',
        element: (
          <ProtectedRoute
            allowedRoles={[UserRole.DIRECTOR_GENERAL, UserRole.PEDAGOGICAL_DIRECTOR, UserRole.PROGRAM_HEAD]}
            requireTenant
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <FiliereDashboard /> },
          // Teaching routes (for staff with isAlsoProfessor: true)
          { path: 'mes-cours', element: <MyClassesPage /> },
          { path: 'mes-cours/:groupeId/module/:moduleElementId', element: <ClassDetailPage /> },
          { path: 'mon-emploi-du-temps', element: <TimetableViewPage /> },
          { path: 'program', element: <PlaceholderPage title="Ma Filière" /> },
          // Gestion Établissement (permission-controlled)
          { path: 'etablissement', element: <Navigate to="etablissement/gestion" replace /> },
          { path: 'etablissement/gestion', element: <GestionEtablissementPage /> },
          { path: 'etablissement/campus', element: <GestionCampusPage /> },
          { path: 'etablissement/departements', element: <DepartementsPage /> },
          { path: 'etablissement/documents-institutionnels', element: <DocumentsInstitutionnelsPage /> },
          // Gestion Académique
          { path: 'gestion-filieres', element: <GestionFilieresPage /> },
          { path: 'gestion-filieres/:filiereId', element: <FiliereDetailPage /> },
          { path: 'academique/filieres', element: <GestionFilieresPage /> },
          { path: 'academique/filieres/:filiereId', element: <FiliereDetailPage /> },
          { path: 'academique/groupes', element: <RequirePermission permission={Permission.GROUP_VIEW}><GestionGroupesPage /></RequirePermission> },
          { path: 'academique/notes', element: <RequirePermission permission={Permission.NOTES_VIEW}><GradesDashboardPage /></RequirePermission> },
          { path: 'academique/jury-deliberation', element: <RequirePermission permission={Permission.JURY_VIEW}><JuryDeliberationPage /></RequirePermission> },
          { path: 'academique/absences', element: <RequirePermission permission={Permission.ATTENDANCE_VIEW}><AttendanceDashboardPage /></RequirePermission> },
          { path: 'academique/parametres-academiques', element: <ParametresAcademiquesPage /> },
          { path: 'academique/demandes-documents', element: <RequestManagementPage /> },
          // Qualité & Suivi
          { path: 'academique/suivi-professeurs', element: <ProfessorMonitoringPage /> },
          { path: 'academique/evaluation-cours', element: <CourseEvaluationPage /> },
          // Backward compatibility redirects
          { path: 'academique/notes-absences', element: <Navigate to="../academique/notes" replace /> },
          { path: 'academique/notes-absences/notes', element: <Navigate to="../../academique/notes" replace /> },
          { path: 'academique/notes-absences/rattrapages', element: <Navigate to="../../academique/notes" replace /> },
          { path: 'academique/notes-absences/absences', element: <Navigate to="../../academique/absences" replace /> },
          { path: 'academique/notes-absences/parametres', element: <Navigate to="../../academique/parametres-academiques" replace /> },
          // Gestion des Utilisateurs
          { path: 'utilisateurs', element: <Navigate to="utilisateurs/personnel" replace /> },
          { path: 'utilisateurs/personnel', element: <StaffManagementPage /> },
          { path: 'utilisateurs/etudiants', element: <StudentManagementPage /> },
          { path: 'utilisateurs/suivi-dossiers', element: <SuiviDossiersPage /> },
          { path: 'utilisateurs/documents-edep', element: <DocumentsEdepPage /> },
          { path: 'utilisateurs/parametres', element: <UserSettingsPage /> },
          { path: 'courses', element: <PlaceholderPage title="Cours" /> },
          { path: 'schedule', element: <PlaceholderPage title="Emploi du temps" /> },
          { path: 'reports', element: <PlaceholderPage title="Rapports" /> },
          // Student Request Management
          { path: 'requests/validate', element: <Navigate to="requests/manage" replace /> },
          { path: 'requests/manage', element: <RequestManagementPage /> },
          // Calendar routes
          { path: 'calendrier', element: <Navigate to="calendrier/planning-annuel" replace /> },
          { path: 'calendrier/planning-annuel', element: <PlanningAnnuelPage /> },
          { path: 'calendrier/emplois-du-temps', element: <RequirePermission permission={Permission.SCHEDULE_VIEW}><TimetableManagementPage /></RequirePermission> },
          { path: 'calendrier/examens', element: <ExamSchedulingPage /> },
          { path: 'calendrier/rattrapages', element: <RattrapageSchedulingPage /> },
          { path: 'calendrier/progression', element: <RequirePermission permission={Permission.SCHEDULE_VIEW}><SchedulingProgressPage /></RequirePermission> },
          { path: 'calendrier/parametres', element: <CalendarSettingsPage /> },
          // Gestion Financière routes (protected by RequireFinance + RequirePermission)
          { path: 'gestion-financiere', element: <RequireFinance><Navigate to="gestion-financiere/frais-scolarite" replace /></RequireFinance> },
          { path: 'gestion-financiere/frais-scolarite', element: <RequireFinance><RequirePermission permission={[Permission.FINANCE_TUITION_VIEW, Permission.FINANCE_ANCILLARY_VIEW, Permission.FINANCE_DISCOUNT_VIEW, Permission.FINANCE_SCHEDULE_VIEW, Permission.FINANCE_REMINDER_VIEW]} mode="any"><TuitionFeesPage /></RequirePermission></RequireFinance> },
          { path: 'gestion-financiere/paiements', element: <RequireFinance><RequirePermission permission={Permission.FINANCE_PAYMENT_VIEW}><PaymentManagementPage /></RequirePermission></RequireFinance> },
          // Ma Paie (staff own payment history)
          { path: 'ma-paie', element: <RequireFinance><StaffPaymentHistoryPage /></RequireFinance> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'security', element: <SecurityPage /> },
          { path: 'privacy', element: <PrivacySettings /> },
        ],
      },

      // Financial Manager routes (requires finance solution to be enabled)
      {
        path: 'finance',
        element: (
          <ProtectedRoute
            allowedRoles={[UserRole.DIRECTOR_GENERAL, UserRole.FINANCIAL_MANAGER]}
            requireTenant
            requireFinance
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <FinanceDashboard /> },
          // Teaching routes (for staff with isAlsoProfessor: true)
          { path: 'mes-cours', element: <MyClassesPage /> },
          { path: 'mes-cours/:groupeId/module/:moduleElementId', element: <ClassDetailPage /> },
          { path: 'mon-emploi-du-temps', element: <TimetableViewPage /> },
          // Gestion Établissement (permission-controlled)
          { path: 'etablissement', element: <Navigate to="etablissement/gestion" replace /> },
          { path: 'etablissement/gestion', element: <GestionEtablissementPage /> },
          { path: 'etablissement/campus', element: <GestionCampusPage /> },
          { path: 'etablissement/departements', element: <DepartementsPage /> },
          { path: 'etablissement/documents-institutionnels', element: <DocumentsInstitutionnelsPage /> },
          // Gestion des Utilisateurs
          { path: 'utilisateurs', element: <Navigate to="utilisateurs/personnel" replace /> },
          { path: 'utilisateurs/personnel', element: <FinanceStaffViewPage /> },
          { path: 'utilisateurs/etudiants', element: <StudentManagementPage /> },
          { path: 'utilisateurs/suivi-dossiers', element: <SuiviDossiersPage /> },
          { path: 'utilisateurs/documents-edep', element: <DocumentsEdepPage /> },
          { path: 'utilisateurs/parametres', element: <UserSettingsPage /> },
          // Frais de Scolarité - Unified page (access requires any finance view permission)
          { path: 'frais-scolarite', element: <RequirePermission permission={[Permission.FINANCE_TUITION_VIEW, Permission.FINANCE_ANCILLARY_VIEW, Permission.FINANCE_DISCOUNT_VIEW, Permission.FINANCE_SCHEDULE_VIEW, Permission.FINANCE_REMINDER_VIEW]} mode="any"><TuitionFeesPage /></RequirePermission> },
          // Legacy routes redirect to unified page
          { path: 'frais-scolarite/grille-tarifaire', element: <Navigate to="../frais-scolarite" replace /> },
          { path: 'frais-scolarite/echeanciers', element: <Navigate to="../frais-scolarite" replace /> },
          { path: 'frais-scolarite/frais-annexes', element: <Navigate to="../frais-scolarite" replace /> },
          { path: 'frais-scolarite/remises-bourses', element: <Navigate to="../frais-scolarite" replace /> },
          // Paiements Étudiants - Unified page with accounts and pending transfers (permission protected)
          { path: 'paiements', element: <RequirePermission permission={Permission.FINANCE_PAYMENT_VIEW}><PaymentManagementPage /></RequirePermission> },
          // Legacy routes redirect to unified page
          { path: 'paiements/comptes', element: <Navigate to="../paiements" replace /> },
          { path: 'paiements/saisie', element: <Navigate to="../paiements" replace /> },
          { path: 'paiements/factures', element: <Navigate to="../paiements" replace /> },
          { path: 'paiements/impayes', element: <Navigate to="../paiements" replace /> },
          { path: 'paiements/relances', element: <RequirePermission permission={Permission.FINANCE_TUITION_VIEW}><RemindersConfigPage /></RequirePermission> },
          { path: 'paiements/suspensions', element: <Navigate to="../paiements" replace /> },
          { path: 'paiements/virements-attente', element: <Navigate to="../paiements" replace /> },
          // Paie Personnel (permission protected)
          { path: 'paie', element: <Navigate to="paie/permanents" replace /> },
          { path: 'paie/permanents', element: <RequirePermission permission={Permission.PAYROLL_PERMANENT_VIEW}><PermanentPayrollPage /></RequirePermission> },
          { path: 'paie/vacataires', element: <RequirePermission permission={Permission.PAYROLL_VACATAIRE_VIEW}><VacatairePayrollPage /></RequirePermission> },
          { path: 'paie/bulletins', element: <RequirePermission permission={Permission.PAYROLL_PERMANENT_VIEW}><PayslipsPage /></RequirePermission> },
          // Reporting (permission protected)
          { path: 'reporting', element: <RequirePermission permission={[Permission.FINANCE_REPORT_RECOVERY, Permission.FINANCE_REPORT_PAYROLL, Permission.FINANCE_REPORT_FORECAST]}><ReportingPage /></RequirePermission> },
          // Comptes Bancaires (permission protected)
          { path: 'comptes-bancaires', element: <RequirePermission permission={Permission.INSTITUTION_BANK_VIEW}><InstitutionBankPage /></RequirePermission> },
          // Legacy routes
          { path: 'payments', element: <Navigate to="paiements" replace /> },
          { path: 'invoices', element: <Navigate to="paiements" replace /> },
          { path: 'scholarships', element: <Navigate to="frais-scolarite/remises-bourses" replace /> },
          { path: 'payroll', element: <Navigate to="paie" replace /> },
          { path: 'reports', element: <Navigate to="reporting" replace /> },
          // Ma Paie (staff own payment history)
          { path: 'ma-paie', element: <RequireFinance><StaffPaymentHistoryPage /></RequireFinance> },
          // Student Request Management
          { path: 'requests/validate', element: <Navigate to="requests/manage" replace /> },
          { path: 'requests/manage', element: <RequestManagementPage /> },
          // Calendar & Planning routes
          { path: 'calendrier', element: <Navigate to="calendrier/planning-annuel" replace /> },
          { path: 'calendrier/planning-annuel', element: <PlanningAnnuelPage /> },
          { path: 'calendrier/emplois-du-temps', element: <RequirePermission permission={Permission.SCHEDULE_VIEW}><TimetableManagementPage /></RequirePermission> },
          { path: 'calendrier/examens', element: <ExamSchedulingPage /> },
          { path: 'calendrier/rattrapages', element: <RattrapageSchedulingPage /> },
          { path: 'calendrier/progression', element: <RequirePermission permission={Permission.SCHEDULE_VIEW}><SchedulingProgressPage /></RequirePermission> },
          { path: 'calendrier/parametres', element: <CalendarSettingsPage /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'security', element: <SecurityPage /> },
          { path: 'privacy', element: <PrivacySettings /> },
        ],
      },

      // Marketing Manager routes
      {
        path: 'marketing',
        element: (
          <ProtectedRoute
            allowedRoles={[UserRole.DIRECTOR_GENERAL, UserRole.MARKETING_MANAGER]}
            requireTenant
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <AdminStaffDashboard /> },
          // Teaching routes (for staff with isAlsoProfessor: true)
          { path: 'mes-cours', element: <MyClassesPage /> },
          { path: 'mes-cours/:groupeId/module/:moduleElementId', element: <ClassDetailPage /> },
          { path: 'mon-emploi-du-temps', element: <TimetableViewPage /> },
          // Gestion Établissement (permission-controlled)
          { path: 'etablissement', element: <Navigate to="etablissement/gestion" replace /> },
          { path: 'etablissement/gestion', element: <GestionEtablissementPage /> },
          { path: 'etablissement/campus', element: <GestionCampusPage /> },
          { path: 'etablissement/departements', element: <DepartementsPage /> },
          { path: 'etablissement/documents-institutionnels', element: <DocumentsInstitutionnelsPage /> },
          { path: 'utilisateurs', element: <Navigate to="utilisateurs/etudiants" replace /> },
          { path: 'utilisateurs/personnel', element: <StaffManagementPage /> },
          { path: 'utilisateurs/etudiants', element: <StudentManagementPage /> },
          { path: 'utilisateurs/suivi-dossiers', element: <SuiviDossiersPage /> },
          // Calendar routes (view only)
          { path: 'calendrier', element: <Navigate to="calendrier/planning-annuel" replace /> },
          { path: 'calendrier/planning-annuel', element: <PlanningAnnuelPage /> },
          // Gestion Financière routes (protected by RequireFinance + RequirePermission)
          { path: 'gestion-financiere', element: <RequireFinance><Navigate to="gestion-financiere/frais-scolarite" replace /></RequireFinance> },
          { path: 'gestion-financiere/frais-scolarite', element: <RequireFinance><RequirePermission permission={[Permission.FINANCE_TUITION_VIEW, Permission.FINANCE_ANCILLARY_VIEW, Permission.FINANCE_DISCOUNT_VIEW, Permission.FINANCE_SCHEDULE_VIEW, Permission.FINANCE_REMINDER_VIEW]} mode="any"><TuitionFeesPage /></RequirePermission></RequireFinance> },
          { path: 'gestion-financiere/paiements', element: <RequireFinance><RequirePermission permission={Permission.FINANCE_PAYMENT_VIEW}><PaymentManagementPage /></RequirePermission></RequireFinance> },
          // Ma Paie (staff own payment history)
          { path: 'ma-paie', element: <RequireFinance><StaffPaymentHistoryPage /></RequireFinance> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'security', element: <SecurityPage /> },
          { path: 'privacy', element: <PrivacySettings /> },
        ],
      },

      // Commercial Manager routes
      {
        path: 'commercial',
        element: (
          <ProtectedRoute
            allowedRoles={[UserRole.DIRECTOR_GENERAL, UserRole.MARKETING_MANAGER, UserRole.COMMERCIAL_MANAGER]}
            requireTenant
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <AdminStaffDashboard /> },
          // Teaching routes (for staff with isAlsoProfessor: true)
          { path: 'mes-cours', element: <MyClassesPage /> },
          { path: 'mes-cours/:groupeId/module/:moduleElementId', element: <ClassDetailPage /> },
          { path: 'mon-emploi-du-temps', element: <TimetableViewPage /> },
          // Gestion Établissement (permission-controlled)
          { path: 'etablissement', element: <Navigate to="etablissement/gestion" replace /> },
          { path: 'etablissement/gestion', element: <GestionEtablissementPage /> },
          { path: 'etablissement/campus', element: <GestionCampusPage /> },
          { path: 'etablissement/departements', element: <DepartementsPage /> },
          { path: 'etablissement/documents-institutionnels', element: <DocumentsInstitutionnelsPage /> },
          { path: 'utilisateurs', element: <Navigate to="utilisateurs/etudiants" replace /> },
          { path: 'utilisateurs/personnel', element: <StaffManagementPage /> },
          { path: 'utilisateurs/etudiants', element: <StudentManagementPage /> },
          { path: 'utilisateurs/suivi-dossiers', element: <SuiviDossiersPage /> },
          // Calendar routes (view only)
          { path: 'calendrier', element: <Navigate to="calendrier/planning-annuel" replace /> },
          { path: 'calendrier/planning-annuel', element: <PlanningAnnuelPage /> },
          // Gestion Financière routes (protected by RequireFinance + RequirePermission)
          { path: 'gestion-financiere', element: <RequireFinance><Navigate to="gestion-financiere/frais-scolarite" replace /></RequireFinance> },
          { path: 'gestion-financiere/frais-scolarite', element: <RequireFinance><RequirePermission permission={[Permission.FINANCE_TUITION_VIEW, Permission.FINANCE_ANCILLARY_VIEW, Permission.FINANCE_DISCOUNT_VIEW, Permission.FINANCE_SCHEDULE_VIEW, Permission.FINANCE_REMINDER_VIEW]} mode="any"><TuitionFeesPage /></RequirePermission></RequireFinance> },
          { path: 'gestion-financiere/paiements', element: <RequireFinance><RequirePermission permission={Permission.FINANCE_PAYMENT_VIEW}><PaymentManagementPage /></RequirePermission></RequireFinance> },
          // Ma Paie (staff own payment history)
          { path: 'ma-paie', element: <RequireFinance><StaffPaymentHistoryPage /></RequireFinance> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'security', element: <SecurityPage /> },
          { path: 'privacy', element: <PrivacySettings /> },
        ],
      },

      // Call Center Agent routes
      {
        path: 'call-center',
        element: (
          <ProtectedRoute
            allowedRoles={[UserRole.CALL_CENTER_AGENT]}
            requireTenant
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <AdminStaffDashboard /> },
          // Ma Paie (staff own payment history)
          { path: 'ma-paie', element: <RequireFinance><StaffPaymentHistoryPage /></RequireFinance> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'security', element: <SecurityPage /> },
          { path: 'privacy', element: <PrivacySettings /> },
        ],
      },

      // Academic Coordinator routes
      {
        path: 'coordinator',
        element: (
          <ProtectedRoute
            allowedRoles={[UserRole.DIRECTOR_GENERAL, UserRole.PEDAGOGICAL_DIRECTOR, UserRole.ACADEMIC_COORDINATOR]}
            requireTenant
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <FiliereDashboard /> },
          // Teaching routes (for staff with isAlsoProfessor: true)
          { path: 'mes-cours', element: <MyClassesPage /> },
          { path: 'mes-cours/:groupeId/module/:moduleElementId', element: <ClassDetailPage /> },
          { path: 'mon-emploi-du-temps', element: <TimetableViewPage /> },
          // Gestion Établissement (permission-controlled)
          { path: 'etablissement', element: <Navigate to="etablissement/gestion" replace /> },
          { path: 'etablissement/gestion', element: <GestionEtablissementPage /> },
          { path: 'etablissement/campus', element: <GestionCampusPage /> },
          { path: 'etablissement/departements', element: <DepartementsPage /> },
          { path: 'etablissement/documents-institutionnels', element: <DocumentsInstitutionnelsPage /> },
          // Gestion Académique
          { path: 'academique/filieres', element: <GestionFilieresPage /> },
          { path: 'academique/filieres/:filiereId', element: <FiliereDetailPage /> },
          { path: 'academique/groupes', element: <RequirePermission permission={Permission.GROUP_VIEW}><GestionGroupesPage /></RequirePermission> },
          { path: 'academique/notes', element: <RequirePermission permission={Permission.NOTES_VIEW}><GradesDashboardPage /></RequirePermission> },
          { path: 'academique/jury-deliberation', element: <RequirePermission permission={Permission.JURY_VIEW}><JuryDeliberationPage /></RequirePermission> },
          { path: 'academique/absences', element: <RequirePermission permission={Permission.ATTENDANCE_VIEW}><AttendanceDashboardPage /></RequirePermission> },
          { path: 'academique/parametres-academiques', element: <ParametresAcademiquesPage /> },
          { path: 'academique/demandes-documents', element: <RequestManagementPage /> },
          // Qualité & Suivi
          { path: 'academique/suivi-professeurs', element: <ProfessorMonitoringPage /> },
          { path: 'academique/evaluation-cours', element: <CourseEvaluationPage /> },
          // Backward compatibility redirects
          { path: 'academique/notes-absences', element: <Navigate to="../academique/notes" replace /> },
          { path: 'academique/notes-absences/notes', element: <Navigate to="../../academique/notes" replace /> },
          { path: 'academique/notes-absences/rattrapages', element: <Navigate to="../../academique/notes" replace /> },
          { path: 'academique/notes-absences/absences', element: <Navigate to="../../academique/absences" replace /> },
          { path: 'academique/notes-absences/parametres', element: <Navigate to="../../academique/parametres-academiques" replace /> },
          // Gestion des Utilisateurs
          { path: 'utilisateurs', element: <Navigate to="utilisateurs/personnel" replace /> },
          { path: 'utilisateurs/personnel', element: <StaffManagementPage /> },
          { path: 'utilisateurs/etudiants', element: <StudentManagementPage /> },
          { path: 'utilisateurs/suivi-dossiers', element: <SuiviDossiersPage /> },
          { path: 'utilisateurs/documents-edep', element: <DocumentsEdepPage /> },
          { path: 'utilisateurs/parametres', element: <UserSettingsPage /> },
          { path: 'courses', element: <PlaceholderPage title="Cours" /> },
          { path: 'teachers', element: <PlaceholderPage title="Professeurs" /> },
          { path: 'schedule', element: <PlaceholderPage title="Emploi du temps" /> },
          { path: 'documents', element: <PlaceholderPage title="Documents" /> },
          { path: 'reports', element: <PlaceholderPage title="Rapports" /> },
          // Student Request Management
          { path: 'requests/validate', element: <Navigate to="requests/manage" replace /> },
          { path: 'requests/manage', element: <RequestManagementPage /> },
          // Gestion Financière routes (protected by RequireFinance + RequirePermission)
          { path: 'gestion-financiere', element: <RequireFinance><Navigate to="gestion-financiere/frais-scolarite" replace /></RequireFinance> },
          { path: 'gestion-financiere/frais-scolarite', element: <RequireFinance><RequirePermission permission={[Permission.FINANCE_TUITION_VIEW, Permission.FINANCE_ANCILLARY_VIEW, Permission.FINANCE_DISCOUNT_VIEW, Permission.FINANCE_SCHEDULE_VIEW, Permission.FINANCE_REMINDER_VIEW]} mode="any"><TuitionFeesPage /></RequirePermission></RequireFinance> },
          { path: 'gestion-financiere/paiements', element: <RequireFinance><RequirePermission permission={Permission.FINANCE_PAYMENT_VIEW}><PaymentManagementPage /></RequirePermission></RequireFinance> },
          // Ma Paie (staff own payment history)
          { path: 'ma-paie', element: <RequireFinance><StaffPaymentHistoryPage /></RequireFinance> },
          // Calendar & Planning routes
          { path: 'calendrier', element: <Navigate to="calendrier/planning-annuel" replace /> },
          { path: 'calendrier/planning-annuel', element: <PlanningAnnuelPage /> },
          { path: 'calendrier/emplois-du-temps', element: <RequirePermission permission={Permission.SCHEDULE_VIEW}><TimetableManagementPage /></RequirePermission> },
          { path: 'calendrier/examens', element: <ExamSchedulingPage /> },
          { path: 'calendrier/rattrapages', element: <RattrapageSchedulingPage /> },
          { path: 'calendrier/progression', element: <RequirePermission permission={Permission.SCHEDULE_VIEW}><SchedulingProgressPage /></RequirePermission> },
          { path: 'calendrier/parametres', element: <CalendarSettingsPage /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'security', element: <SecurityPage /> },
          { path: 'privacy', element: <PrivacySettings /> },
        ],
      },

      // Branch Manager routes
      {
        path: 'branch',
        element: (
          <ProtectedRoute
            allowedRoles={[UserRole.DIRECTOR_GENERAL, UserRole.BRANCH_MANAGER]}
            requireTenant
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <AdminStaffDashboard /> },
          // Teaching routes (for staff with isAlsoProfessor: true)
          { path: 'mes-cours', element: <MyClassesPage /> },
          { path: 'mes-cours/:groupeId/module/:moduleElementId', element: <ClassDetailPage /> },
          { path: 'mon-emploi-du-temps', element: <TimetableViewPage /> },
          { path: 'etablissement', element: <Navigate to="etablissement/gestion" replace /> },
          { path: 'etablissement/gestion', element: <GestionEtablissementPage /> },
          { path: 'etablissement/campus', element: <GestionCampusPage /> },
          { path: 'etablissement/departements', element: <DepartementsPage /> },
          { path: 'etablissement/documents-institutionnels', element: <DocumentsInstitutionnelsPage /> },
          // Gestion Académique
          { path: 'academique', element: <Navigate to="academique/filieres" replace /> },
          { path: 'academique/filieres', element: <GestionFilieresPage /> },
          { path: 'academique/filieres/:filiereId', element: <FiliereDetailPage /> },
          { path: 'academique/groupes', element: <RequirePermission permission={Permission.GROUP_VIEW}><GestionGroupesPage /></RequirePermission> },
          { path: 'academique/notes', element: <RequirePermission permission={Permission.NOTES_VIEW}><GradesDashboardPage /></RequirePermission> },
          { path: 'academique/absences', element: <RequirePermission permission={Permission.ATTENDANCE_VIEW}><AttendanceDashboardPage /></RequirePermission> },
          { path: 'academique/parametres-academiques', element: <ParametresAcademiquesPage /> },
          { path: 'academique/demandes-documents', element: <RequestManagementPage /> },
          { path: 'academique/jury-deliberation', element: <RequirePermission permission={Permission.JURY_VIEW}><JuryDeliberationPage /></RequirePermission> },
          { path: 'academique/suivi-professeurs', element: <ProfessorMonitoringPage /> },
          { path: 'academique/evaluation-cours', element: <CourseEvaluationPage /> },
          // Legacy routes
          { path: 'gestion-filieres', element: <Navigate to="academique/filieres" replace /> },
          { path: 'gestion-filieres/:filiereId', element: <Navigate to="../academique/filieres/:filiereId" replace /> },
          { path: 'utilisateurs', element: <Navigate to="utilisateurs/personnel" replace /> },
          { path: 'utilisateurs/personnel', element: <StaffManagementPage /> },
          { path: 'utilisateurs/etudiants', element: <StudentManagementPage /> },
          { path: 'utilisateurs/suivi-dossiers', element: <SuiviDossiersPage /> },
          { path: 'utilisateurs/documents-edep', element: <DocumentsEdepPage /> },
          { path: 'utilisateurs/parametres', element: <UserSettingsPage /> },
          { path: 'documents', element: <PlaceholderPage title="Documents" /> },
          // Gestion Financière routes (protected by RequireFinance + RequirePermission)
          { path: 'gestion-financiere', element: <RequireFinance><Navigate to="gestion-financiere/paiements" replace /></RequireFinance> },
          { path: 'gestion-financiere/frais-scolarite', element: <RequireFinance><RequirePermission permission={[Permission.FINANCE_TUITION_VIEW, Permission.FINANCE_ANCILLARY_VIEW, Permission.FINANCE_DISCOUNT_VIEW, Permission.FINANCE_SCHEDULE_VIEW, Permission.FINANCE_REMINDER_VIEW]} mode="any"><TuitionFeesPage /></RequirePermission></RequireFinance> },
          { path: 'gestion-financiere/paiements', element: <RequireFinance><RequirePermission permission={Permission.FINANCE_PAYMENT_VIEW}><PaymentManagementPage /></RequirePermission></RequireFinance> },
          { path: 'gestion-financiere/paie/permanents', element: <RequireFinance><RequirePermission permission={Permission.PAYROLL_PERMANENT_VIEW}><PermanentPayrollPage /></RequirePermission></RequireFinance> },
          { path: 'gestion-financiere/paie/vacataires', element: <RequireFinance><RequirePermission permission={Permission.PAYROLL_VACATAIRE_VIEW}><VacatairePayrollPage /></RequirePermission></RequireFinance> },
          { path: 'gestion-financiere/reporting', element: <RequireFinance><RequirePermission permission={[Permission.FINANCE_REPORT_RECOVERY, Permission.FINANCE_REPORT_PAYROLL, Permission.FINANCE_REPORT_FORECAST]}><ReportingPage /></RequirePermission></RequireFinance> },
          { path: 'gestion-financiere/comptes-bancaires', element: <RequireFinance><RequirePermission permission={Permission.INSTITUTION_BANK_VIEW}><InstitutionBankPage /></RequirePermission></RequireFinance> },
          // Legacy finance routes - redirect to new structure
          { path: 'paiements', element: <RequireFinance><Navigate to="gestion-financiere/paiements" replace /></RequireFinance> },
          { path: 'paiements/comptes', element: <RequireFinance><Navigate to="gestion-financiere/paiements" replace /></RequireFinance> },
          { path: 'paiements/saisie', element: <RequireFinance><Navigate to="gestion-financiere/paiements" replace /></RequireFinance> },
          { path: 'paiements/factures', element: <RequireFinance><Navigate to="gestion-financiere/paiements" replace /></RequireFinance> },
          { path: 'paiements/impayes', element: <RequireFinance><Navigate to="gestion-financiere/paiements" replace /></RequireFinance> },
          { path: 'paiements/virements-attente', element: <RequireFinance><Navigate to="gestion-financiere/paiements" replace /></RequireFinance> },
          { path: 'reporting', element: <RequireFinance><Navigate to="gestion-financiere/reporting" replace /></RequireFinance> },
          // Legacy route
          { path: 'payments', element: <RequireFinance><Navigate to="gestion-financiere/paiements" replace /></RequireFinance> },
          { path: 'reports', element: <RequireFinance><Navigate to="gestion-financiere/reporting" replace /></RequireFinance> },
          // Student Request Management
          { path: 'requests/validate', element: <Navigate to="requests/manage" replace /> },
          { path: 'requests/manage', element: <RequestManagementPage /> },
          // Ma Paie (staff own payment history)
          { path: 'ma-paie', element: <RequireFinance><StaffPaymentHistoryPage /></RequireFinance> },
          // Calendar & Planning routes
          { path: 'calendrier', element: <Navigate to="calendrier/planning-annuel" replace /> },
          { path: 'calendrier/planning-annuel', element: <PlanningAnnuelPage /> },
          { path: 'calendrier/emplois-du-temps', element: <RequirePermission permission={Permission.SCHEDULE_VIEW}><TimetableManagementPage /></RequirePermission> },
          { path: 'calendrier/examens', element: <ExamSchedulingPage /> },
          { path: 'calendrier/rattrapages', element: <RattrapageSchedulingPage /> },
          { path: 'calendrier/progression', element: <RequirePermission permission={Permission.SCHEDULE_VIEW}><SchedulingProgressPage /></RequirePermission> },
          { path: 'calendrier/parametres', element: <CalendarSettingsPage /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'security', element: <SecurityPage /> },
          { path: 'privacy', element: <PrivacySettings /> },
        ],
      },

      // Teacher routes
      {
        path: 'teacher',
        element: (
          <ProtectedRoute
            allowedRoles={[UserRole.TEACHER]}
            requireTenant
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <TeacherDashboard /> },

          // My Classes (new professor panel routes)
          { path: 'mes-cours', element: <MyClassesPage /> },
          { path: 'mes-cours/:groupeId/module/:moduleElementId', element: <ClassDetailPage /> },
          { path: 'mon-emploi-du-temps', element: <TimetableViewPage /> },

          // Legacy routes - redirect to new routes
          { path: 'courses', element: <Navigate to="mes-cours" replace /> },
          { path: 'grades', element: <Navigate to="mes-cours" replace /> },
          { path: 'attendance', element: <Navigate to="mes-cours" replace /> },

          // User management (if teacher has permissions)
          { path: 'utilisateurs', element: <Navigate to="utilisateurs/personnel" replace /> },
          { path: 'utilisateurs/personnel', element: <StaffManagementPage /> },
          { path: 'utilisateurs/etudiants', element: <StudentManagementPage /> },
          { path: 'utilisateurs/suivi-dossiers', element: <SuiviDossiersPage /> },
          { path: 'utilisateurs/documents-edep', element: <DocumentsEdepPage /> },
          { path: 'etudiants', element: <StudentManagementPage /> },

          // Documents
          { path: 'documents', element: <PlaceholderPage title="Documents" /> },

          // Calendar routes (read-only)
          { path: 'planning-annuel', element: <PlanningAnnuelPage /> },
          { path: 'emploi-du-temps', element: <TimetableViewPage /> },
          // Read-only: published exam / rattrapage sessions on the professor's own courses
          { path: 'planning-examens', element: <TeacherExamsPage /> },
          { path: 'planning-rattrapages', element: <Navigate to="../planning-examens?type=rattrapages" replace /> },

          // Ma Paie (staff own payment history)
          { path: 'ma-paie', element: <RequireFinance><StaffPaymentHistoryPage /></RequireFinance> },

          // Profile & Security
          { path: 'profile', element: <ProfilePage /> },
          { path: 'security', element: <SecurityPage /> },
          { path: 'privacy', element: <PrivacySettings /> },
        ],
      },

      // Student routes
      {
        path: 'student',
        element: (
          <ProtectedRoute
            allowedRoles={[UserRole.STUDENT]}
            requireTenant
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <StudentDashboard /> },
          // My Classes (courses)
          { path: 'mes-cours', element: <StudentMyClassesPage /> },
          { path: 'mes-cours/:moduleElementId', element: <StudentClassDetailPage /> },
          { path: 'courses', element: <Navigate to="mes-cours" replace /> },
          // Schedule
          { path: 'mon-emploi-du-temps', element: <MySchedulePage /> },
          { path: 'schedule', element: <Navigate to="mon-emploi-du-temps" replace /> },
          { path: 'emploi-du-temps', element: <Navigate to="mon-emploi-du-temps" replace /> },
          // Calendar
          { path: 'calendrier-academique', element: <MyCalendarPage /> },
          { path: 'calendrier/planning-annuel', element: <MyCalendarPage /> },
          { path: 'planning-annuel', element: <Navigate to="calendrier-academique" replace /> },
          // Exams & Rattrapages
          { path: 'planning-examens', element: <ExamSchedulePage /> },
          { path: 'rattrapages', element: <RattrapageSchedulePage /> },
          // Legacy grades route (grades are now in class detail)
          { path: 'grades', element: <Navigate to="mes-cours" replace /> },
          // Finance (protected by RequireFinance)
          { path: 'compte-financier', element: <RequireFinance><MyPaymentsPage /></RequireFinance> },
          { path: 'payments', element: <RequireFinance><Navigate to="../compte-financier" replace /></RequireFinance> },
          // Transcript
          { path: 'transcript', element: <TranscriptPage /> },
          { path: 'releve-notes', element: <Navigate to="transcript" replace /> },
          // Student Requests
          { path: 'requests', element: <RequestsDashboardPage /> },
          { path: 'requests/new', element: <CreateRequestPage /> },
          { path: 'requests/:id', element: <RequestDetailPage /> },
          { path: 'demandes', element: <Navigate to="requests" replace /> },
          // Other
          { path: 'documents', element: <PlaceholderPage title="Documents" /> },
          { path: 'messages', element: <PlaceholderPage title="Messages" /> },
          // Profile & Security
          { path: 'profile', element: <ProfilePage /> },
          { path: 'security', element: <SecurityPage /> },
          { path: 'privacy', element: <PrivacySettings /> },
        ],
      },

      // Parent routes
      {
        path: 'parent',
        element: (
          <ProtectedRoute
            allowedRoles={[UserRole.PARENT]}
            requireTenant
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <ParentDashboard /> },
          { path: 'academic', element: <PlaceholderPage title="Suivi Académique" /> },
          { path: 'grades', element: <ParentGradesPage /> },
          { path: 'payments', element: <ParentPaymentsPage /> },
          { path: 'messages', element: <PlaceholderPage title="Messages" /> },
          // Calendar & planning (read-only): shared annual calendar + per-child views
          { path: 'calendrier-academique', element: <MyCalendarPage /> },
          { path: 'emploi-du-temps', element: <ParentSchedulePage /> },
          { path: 'presences', element: <ParentAttendancePage /> },
          { path: 'planning-examens', element: <ParentExamSchedulePage /> },
          { path: 'rattrapages', element: <ParentRattrapageSchedulePage /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'security', element: <SecurityPage /> },
        ],
      },
    ],
  },

  // Root: apex domain shows the public homepage; tenant subdomains redirect to
  // login (or the dashboard if already authenticated).
  {
    path: '/',
    element: <RootRoute />,
  },
  {
    path: '/conformite-cndp',
    element: <ConformiteCndpPage />,
  },

  // 404
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);
// Route update
